import { useState, useEffect } from "react";
import Modal from "../ui/Modal";
import Select from "../ui/Select";
import TablePagination from "../common/TablePagination";
import { Search, Filter, Shield, Briefcase, Mail, Sparkles, Trash2, Edit2 } from "lucide-react";
import {
  getSocietyStaff,
  inviteSocietyStaff,
  updateSocietyStaff,
  deleteSocietyStaff,
} from "../../services/societyService";
import {
  normalizePropertyType,
  PROPERTY_CATEGORIES,
  isNonDeskRole,
} from "../../utils/propertyStructure";
import { getCurrentRoleKey } from "../../services/authService";

const PropertyStaff = ({ societyId, propertyType }) => {
  const normalizedCategory = normalizePropertyType(propertyType);
  const isIndustrial = normalizedCategory === PROPERTY_CATEGORIES.INDUSTRIAL;
  const isCommercial = normalizedCategory === PROPERTY_CATEGORIES.COMMERCIAL;

  const roleKey = getCurrentRoleKey();
  const isSuperAdmin = roleKey === "super-admin";
  const isAdmin = roleKey === "admin";
  const isSocietyAdmin = roleKey === "society-admin";
  const isPropertyManager = roleKey === "property-manager" || roleKey === "manager";
  const canManageStaff = isSuperAdmin || isAdmin || isSocietyAdmin || isPropertyManager;

  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const [staffToDelete, setStaffToDelete] = useState(null);
  const [deletingStaff, setDeletingStaff] = useState(false);
  const [staffToEdit, setStaffToEdit] = useState(null);
  const [updatingStaff, setUpdatingStaff] = useState(false);
  const [editData, setEditData] = useState({
    name: "",
    phone: "",
    role: "",
    shift: "General Shift",
  });

  const defaultRole = isIndustrial
    ? "Security Guard"
    : isCommercial
      ? "Mall Security"
      : "Guard";

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [inviteData, setInviteData] = useState({
    name: "",
    phone: "",
    role: defaultRole,
    shift: "General Shift",
  });

  useEffect(() => {
    if (societyId) fetchStaff();
  }, [societyId]);

  const fetchStaff = async () => {
    setLoading(true);
    const result = await getSocietyStaff(societyId);
    setStaff(Array.isArray(result) ? result : result?.data || []);
    setLoading(false);
  };

  const staffRoles = isIndustrial
    ? [
      "Security Guard",
      "Security Head",
      "Gate Receptionist",
      "Floor Receptionist",
      "Operations Staff",
      "Plant Security",
      "Loading Bay Controller",
      "Safety Officer",
      "Maintenance Engineer",
      "Facility Lead",
      "Cleaning Staff / Housekeeping",
      "Pantry / Cafeteria Staff",
    ]
    : isCommercial
      ? [
        "Mall Security",
        "Loading Dock Staff",
        "Maintenance",
        "Housekeeping Supervisor",
        "Cleaning Staff / Housekeeping",
        "Pantry Staff",
        "Facility Manager",
      ]
      : [
        "Guard",
        "Head Security",
        "Maintenance",
        "Cleaning Staff / Housekeeping",
        "Pantry / Service Staff",
        "Facility Manager",
      ];

  const getFilteredStaff = () => {
    return staff.filter((s) => {
      const matchesSearch =
        (s.name || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
        (s.phone || "").includes(searchQuery) ||
        (s.role || "").toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole =
        roleFilter === "All" ||
        (s.role || "").toLowerCase() === roleFilter.toLowerCase();
      return matchesSearch && matchesRole;
    });
  };

  const filteredStaff = getFilteredStaff();
  const paginatedStaff = filteredStaff.slice((page - 1) * limit, page * limit);

  const handleInviteStaff = async () => {
    if (!inviteData.phone.trim()) return;
    const res = await inviteSocietyStaff(societyId, inviteData);
    if (res || res === undefined) {
      fetchStaff();
      setIsInviteModalOpen(false);
      setInviteData({ name: "", phone: "", role: defaultRole, shift: "General Shift" });
    }
  };

  const handleConfirmDeleteStaff = async () => {
    if (!staffToDelete) return;
    try {
      setDeletingStaff(true);
      await deleteSocietyStaff(societyId, staffToDelete.id || staffToDelete.user_id);
      setStaffToDelete(null);
      fetchStaff();
    } catch (err) {
      console.error("Delete staff failed:", err);
    } finally {
      setDeletingStaff(false);
    }
  };

  const openEditStaffModal = (s) => {
    setStaffToEdit(s);
    setEditData({
      name: s.name || s.full_name || "",
      phone: s.phone || s.mobile_number || "",
      role: s.role || s.role_name || defaultRole,
      shift: s.shift || "General Shift",
    });
  };

  const handleSaveStaffEdit = async () => {
    if (!staffToEdit) return;
    try {
      setUpdatingStaff(true);
      await updateSocietyStaff(societyId, staffToEdit.id || staffToEdit.user_id, {
        name: editData.name,
        full_name: editData.name,
        phone: editData.phone,
        mobile_number: editData.phone,
        role: editData.role,
        role_name: editData.role,
        shift: editData.shift,
      });
      setStaffToEdit(null);
      fetchStaff();
    } catch (err) {
      console.error("Update staff failed:", err);
    } finally {
      setUpdatingStaff(false);
    }
  };

  return (
    <div
      style={{
        padding: "24px",
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
      }}
    >
      {/* Header & Actions */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          flexWrap: "wrap",
          gap: "12px",
        }}
      >
        <div>
          <h3
            style={{
              color: "#0f172a",
              marginBottom: "6px",
              fontSize: "18px",
              fontWeight: "700",
              fontFamily: "var(--font-heading, Montserrat, sans-serif)",
            }}
          >
            {isIndustrial
              ? "Operations & Security Staff"
              : isCommercial
                ? "Facility & Commercial Staff"
                : "Staff & Security"}
          </h3>
          <p style={{ color: "#64748b", margin: 0, fontSize: "13.5px" }}>
            {isIndustrial
              ? "Manage plant security personnel, dock controllers, and facility maintenance teams."
              : isCommercial
                ? "Manage mall security officers, goods dock personnel, and facility supervisors."
                : "Manage security guards and facility staff assigned to this property."}
          </p>
        </div>
        <button
          onClick={() => {
            setInviteData({ phone: "", role: staffRoles[0], shift: "General Shift", name: "" });
            setIsInviteModalOpen(true);
          }}
          style={{
            padding: "10px 16px",
            background: "var(--color-dark-bg, #27306f)",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            fontWeight: "600",
            fontSize: "13.5px",
          }}
        >
          <Mail size={16} /> Invite Personnel
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: "16px", marginBottom: "24px", flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1, minWidth: "220px", maxWidth: "320px" }}>
          <Search
            size={16}
            color="#94a3b8"
            style={{
              position: "absolute",
              left: "12px",
              top: "50%",
              transform: "translateY(-50%)",
            }}
          />
          <input
            type="text"
            placeholder="Search by name or phone..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            style={{
              width: "100%",
              padding: "8px 12px 8px 36px",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              fontSize: "13.5px",
              outline: "none",
            }}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: "180px" }}>
          <Filter size={16} color="#64748b" />
          <Select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            options={[
              { value: "All", label: "All Roles" },
              ...staffRoles.map((r) => ({ value: r, label: r })),
            ]}
            size="sm"
          />
        </div>
      </div>

      {/* Staff Table */}
      <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
            Loading staff personnel...
          </div>
        ) : filteredStaff.length > 0 ? (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
              <thead>
                <tr
                  style={{
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    color: "#475569",
                    fontSize: "13px",
                  }}
                >
                  <th style={{ padding: "12px 16px", fontWeight: "600" }}>Name</th>
                  <th style={{ padding: "12px 16px", fontWeight: "600" }}>Designated Role</th>
                  <th style={{ padding: "12px 16px", fontWeight: "600" }}>Phone</th>
                  <th style={{ padding: "12px 16px", fontWeight: "600" }}>Shift / Gate</th>
                  <th style={{ padding: "12px 16px", fontWeight: "600" }}>Status</th>
                  {canManageStaff && (
                    <th style={{ padding: "12px 16px", fontWeight: "600", textAlign: "right" }}>Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {paginatedStaff.map((s) => (
                  <tr key={s.id || Math.random()} style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <td style={{ padding: "12px 16px", color: "#0f172a", fontWeight: "600" }}>
                      {s.name || "N/A"}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "4px",
                          background:
                            (s.role || "").includes("Security") || (s.role || "").includes("Guard")
                              ? "#fef2f2"
                              : "#f1f5f9",
                          color:
                            (s.role || "").includes("Security") || (s.role || "").includes("Guard")
                              ? "#dc2626"
                              : "#475569",
                          padding: "3px 10px",
                          borderRadius: "12px",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        {(s.role || "").includes("Security") || (s.role || "").includes("Guard") ? (
                          <Shield size={12} />
                        ) : (
                          <Briefcase size={12} />
                        )}
                        {s.role || "N/A"}
                      </span>
                    </td>
                    <td style={{ padding: "12px 16px", color: "#475569" }}>{s.phone || "N/A"}</td>
                    <td style={{ padding: "12px 16px", color: "#475569" }}>{s.shift || "General Shift"}</td>
                    <td style={{ padding: "12px 16px" }}>
                      <span
                        style={{
                          color:
                            s.status === "Active"
                              ? "#16a34a"
                              : s.status === "Pending"
                                ? "#d97706"
                                : "#94a3b8",
                          fontSize: "13px",
                          fontWeight: "600",
                        }}
                      >
                        {s.status || "Active"}
                      </span>
                    </td>
                    {canManageStaff && (
                      <td style={{ padding: "12px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          type="button"
                          onClick={() => openEditStaffModal(s)}
                          title={`Edit ${s.name || "personnel"}`}
                          style={{
                            background: "#fff",
                            border: "1px solid #e2e8f0",
                            borderRadius: "6px",
                            padding: "6px 8px",
                            color: "var(--color-dark-bg)",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginRight: "8px",
                            transition: "all 150ms ease",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#eff6ff";
                            e.currentTarget.style.borderColor = "#bfdbfe";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#fff";
                            e.currentTarget.style.borderColor = "#e2e8f0";
                          }}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setStaffToDelete(s)}
                          title={`Remove ${s.name || "personnel"} from property`}
                          style={{
                            background: "#fff",
                            border: "1px solid #fee2e2",
                            borderRadius: "6px",
                            padding: "6px 8px",
                            color: "#dc2626",
                            cursor: "pointer",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 150ms ease",
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#fef2f2";
                            e.currentTarget.style.borderColor = "#fca5a5";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "#fff";
                            e.currentTarget.style.borderColor = "#fee2e2";
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination
              totalItems={filteredStaff.length}
              page={page}
              limit={limit}
              onPageChange={setPage}
              onLimitChange={setLimit}
            />
          </>
        ) : (
          <div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>
            No personnel match the selected filters.
          </div>
        )}
      </div>

      {/* Invite Modal */}
      {isInviteModalOpen && (
        <Modal
          isOpen={true}
          onClose={() => setIsInviteModalOpen(false)}
          title={isIndustrial ? "Invite Operations / Security Personnel" : "Invite Staff"}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <p style={{ margin: 0, color: "#64748b", fontSize: "14px" }}>
              Send an SMS invitation to a staff member to join this property.
            </p>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                Full Name
              </label>
              <input
                type="text"
                placeholder="e.g. Vikram Rathore"
                value={inviteData.name}
                onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "14px",
                  fontFamily: "inherit",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                Phone Number
              </label>
              <input
                type="tel"
                placeholder="+91 XXXXX XXXXX"
                value={inviteData.phone}
                onChange={(e) => setInviteData({ ...inviteData, phone: e.target.value })}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "14px",
                  fontFamily: "inherit",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                Role
              </label>
              <Select
                value={inviteData.role}
                onChange={(e) => setInviteData({ ...inviteData, role: e.target.value })}
                options={staffRoles}
              />
              {isNonDeskRole(inviteData.role) && (
                <div
                  style={{
                    marginTop: "8px",
                    padding: "8px 12px",
                    background: "#f0fdf4",
                    border: "1px solid #bbf7d0",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "#166534",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <Sparkles size={16} color="#16a34a" style={{ flexShrink: 0 }} />
                  <span>
                    <strong>Non-Desk Support Role:</strong> This role operates in the field/facility and does not require or occupy a workstation desk.
                  </span>
                </div>
              )}
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                Assigned Shift
              </label>
              <Select
                value={inviteData.shift}
                onChange={(e) => setInviteData({ ...inviteData, shift: e.target.value })}
                options={[
                  { value: "General Shift", label: "General Shift (9 AM - 6 PM)" },
                  { value: "Morning Shift", label: "Morning Shift (6 AM - 2 PM)" },
                  { value: "Evening Shift", label: "Evening Shift (2 PM - 10 PM)" },
                  { value: "Night Shift", label: "Night Shift (10 PM - 6 AM)" },
                ]}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "8px",
              }}
            >
              <button
                onClick={() => setIsInviteModalOpen(false)}
                style={{
                  padding: "8px 16px",
                  background: "#fff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleInviteStaff}
                disabled={!inviteData.phone.trim()}
                style={{
                  padding: "8px 16px",
                  background: "var(--color-dark-bg, #27306f)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: inviteData.phone.trim() ? "pointer" : "not-allowed",
                  opacity: inviteData.phone.trim() ? 1 : 0.5,
                  fontWeight: "600",
                }}
              >
                Send Invite
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete Staff Modal */}
      {staffToDelete && (
        <Modal
          isOpen={true}
          onClose={() => setStaffToDelete(null)}
          title="Confirm Staff Removal"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <p style={{ margin: 0, color: "#475569", fontSize: "14px", lineHeight: "1.5" }}>
              Are you sure you want to remove <strong>{staffToDelete.name || "this staff member"}</strong> ({staffToDelete.role || "Staff"}) from this property?
              <br />
              <span style={{ fontSize: "13px", color: "#64748b" }}>
                This will unassign any active gate/security posts for this user in this property.
              </span>
            </p>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px", marginTop: "8px" }}>
              <button
                type="button"
                onClick={() => setStaffToDelete(null)}
                disabled={deletingStaff}
                style={{
                  padding: "8px 16px",
                  background: "#f1f5f9",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#475569",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmDeleteStaff}
                disabled={deletingStaff}
                style={{
                  padding: "8px 16px",
                  background: "#dc2626",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  color: "#fff",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Trash2 size={14} />
                {deletingStaff ? "Removing..." : "Remove Staff"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Edit Staff Modal */}
      {staffToEdit && (
        <Modal
          isOpen={true}
          onClose={() => setStaffToEdit(null)}
          title="Edit Staff Member"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                Full Name
              </label>
              <input
                type="text"
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "14px",
                  fontFamily: "inherit",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                Phone Number
              </label>
              <input
                type="tel"
                value={editData.phone}
                onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                style={{
                  width: "100%",
                  padding: "10px",
                  borderRadius: "6px",
                  border: "1px solid #cbd5e1",
                  fontSize: "14px",
                  fontFamily: "inherit",
                }}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                Role
              </label>
              <Select
                value={editData.role}
                onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                options={staffRoles}
              />
            </div>
            <div>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontWeight: "600",
                  color: "#0f172a",
                  fontSize: "14px",
                }}
              >
                Assigned Shift
              </label>
              <Select
                value={editData.shift}
                onChange={(e) => setEditData({ ...editData, shift: e.target.value })}
                options={[
                  { value: "General Shift", label: "General Shift (9 AM - 6 PM)" },
                  { value: "Morning Shift", label: "Morning Shift (6 AM - 2 PM)" },
                  { value: "Evening Shift", label: "Evening Shift (2 PM - 10 PM)" },
                  { value: "Night Shift", label: "Night Shift (10 PM - 6 AM)" },
                ]}
              />
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "8px",
              }}
            >
              <button
                type="button"
                onClick={() => setStaffToEdit(null)}
                disabled={updatingStaff}
                style={{
                  padding: "8px 16px",
                  background: "#fff",
                  border: "1px solid #cbd5e1",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveStaffEdit}
                disabled={updatingStaff || !editData.name.trim() || !editData.phone.trim()}
                style={{
                  padding: "8px 16px",
                  background: "var(--color-dark-bg)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: updatingStaff ? "not-allowed" : "pointer",
                  fontWeight: "600",
                }}
              >
                {updatingStaff ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PropertyStaff;
